import React, { useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Alert, Box, Button, Link, Stack, Text } from '@chakra-ui/react'
import { useAuthContext } from 'context/auth'
import {
  ReadinessDateField,
  readinessDate,
  readinessDateLabels,
  readinessErrorMessage,
  vacancyReadinessApi
} from 'lib/vacancy-readiness'

const ReadinessDateHistory = ({
  vacancyPeriodId,
  field
}: {
  vacancyPeriodId: string
  field: ReadinessDateField
}) => {
  const { firebaseUser } = useAuthContext()
  const [expanded, setExpanded] = useState(false)
  const history = useInfiniteQuery({
    queryKey: ['vacancy-readiness-history', firebaseUser?.uid, vacancyPeriodId, field],
    queryFn: ({ pageParam = 1 }) => vacancyReadinessApi.history(vacancyPeriodId, pageParam, field),
    getNextPageParam: (last) =>
      last.page * last.per_page < last.total_count ? last.page + 1 : undefined,
    enabled: !!firebaseUser
  })
  const items = Array.from(
    new Map(
      (history.data?.pages.flatMap((page) => page.items) || []).map((item) => [item.id, item])
    ).values()
  )
  const total = history.data?.pages[0].total_count || 0
  const visible = expanded ? items : items.slice(0, 1)
  return (
    <Stack spacing={2} fontSize='sm' aria-label={`${readinessDateLabels[field]} history`}>
      {history.isLoading && (
        <Text color='gray.600' role='status'>
          Loading changes…
        </Text>
      )}
      {history.isError && (
        <Alert status='error' display='block'>
          <Text>{readinessErrorMessage(history.error)}</Text>
          <Button size='sm' mt={2} onClick={() => history.refetch()}>
            Retry history
          </Button>
        </Alert>
      )}
      {history.isSuccess && !items.length && <Text color='gray.600'>N/A</Text>}
      {visible.map((item) => {
        const isImport = ['legacy_import', 'legacy_import_correction'].includes(item.change_type)
        // These administrative estimate updates are backfills, not operator edits.
        const isEngineeringBackfill =
          item.change_type === 'readiness_estimates_updated' &&
          [
            'vacancy-date-reconciliation:20261104:v1',
            'vacancy-date-reconciliation-correction:20261104:v1'
          ].includes(item.actor_name || '')
        // The importer stores both a starting vacancy snapshot and each source record.
        // Its stable source-snapshot reason identifies those separately without discarding audit rows.
        const isSourceImport =
          item.change_type === 'legacy_import' &&
          item.reason ===
            'Attributable legacy source snapshot; missing revisions and effective dates remain unknown'
        const before =
          typeof item.before_values?.[field] === 'string'
            ? (item.before_values[field] as string)
            : null
        const after =
          typeof item.after_values[field] === 'string' ? (item.after_values[field] as string) : null
        const originalId =
          (field === 'current_confirmed_tour_ready_date'
            ? item.corrects_history_ids?.tour
            : field === 'current_confirmed_rent_ready_date'
            ? item.corrects_history_ids?.rent
            : null) || item.corrects_history_id
        const originalAnchor = `readiness-history-${field}-${originalId}`
        return (
          <Box
            id={`readiness-history-${field}-${item.id}`}
            key={item.id}
            borderLeftWidth={2}
            pl={3}
            overflowWrap='anywhere'
          >
            <Text color='gray.600'>
              {item.change_type === 'readiness_lost' ? (
                <>
                  No longer ready as of {readinessDate(item.effective_date)}; earlier ready period
                  retained
                </>
              ) : item.change_type === 'readiness_confirmation_retracted' ? (
                <>Marked ready by mistake; confirmation retracted</>
              ) : item.change_type === 'legacy_import_correction' ? (
                <>
                  Imported starting date corrected from {before ? readinessDate(before) : 'N/A'} to{' '}
                  {after ? readinessDate(after) : 'N/A'}
                </>
              ) : isImport ? (
                <>
                  {isSourceImport ? 'Legacy date record' : 'Vacancy starting date'} imported as{' '}
                  {after ? readinessDate(after) : 'N/A'}
                </>
              ) : before === after ? (
                <>Date recorded as {after ? readinessDate(after) : 'N/A'}</>
              ) : (
                <>
                  Date changed from {before ? readinessDate(before) : 'N/A'} to{' '}
                  {after ? readinessDate(after) : 'N/A'}
                </>
              )}
              {!isImport && (
                <> by {isEngineeringBackfill ? 'ENG backfill' : item.actor_name || 'System'}</>
              )}{' '}
              —{' '}
              {new Date(item.recorded_at).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </Text>
            {!isImport && !isEngineeringBackfill && (
              <Text whiteSpace='pre-wrap'>{item.reason || 'No note recorded.'}</Text>
            )}
            {(item.corrects_history_id ||
              item.corrects_history_ids?.tour ||
              item.corrects_history_ids?.rent) && (
              <Text color='gray.600'>
                {item.change_type === 'readiness_lost'
                  ? 'Ends an earlier ready period; its confirmation remains valid in history.'
                  : item.change_type === 'readiness_confirmation_retracted'
                  ? 'Retracts the earlier confirmation and its corrections; originals are retained.'
                  : isEngineeringBackfill
                  ? 'Corrects an earlier backfill; the original is retained.'
                  : isImport
                  ? 'Corrects the imported starting values; the original is retained.'
                  : 'Corrects an earlier confirmation; the original is retained in these changes.'}{' '}
                {originalId && items.some((entry) => entry.id === originalId) && (
                  <Link
                    color='teal.700'
                    href={`#${originalAnchor}`}
                    onClick={(event) => {
                      event.preventDefault()
                      setExpanded(true)
                      requestAnimationFrame(() =>
                        document
                          .getElementById(originalAnchor)
                          ?.scrollIntoView({ block: 'nearest' })
                      )
                    }}
                  >
                    View original
                  </Link>
                )}
              </Text>
            )}
          </Box>
        )
      })}
      {total > 1 && (
        <Button
          variant='link'
          colorScheme='teal'
          size='sm'
          alignSelf='start'
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide older changes' : 'Show older changes'}
        </Button>
      )}
      {expanded && history.hasNextPage && (
        <Button
          size='sm'
          variant='outline'
          alignSelf='start'
          isLoading={history.isFetchingNextPage}
          onClick={() => history.fetchNextPage()}
        >
          Load more
        </Button>
      )}
    </Stack>
  )
}

export default ReadinessDateHistory
